import { useId, useMemo, useState } from 'react';
import {
  createInteraction,
  createInteractionItem,
  interactionSchema,
  validateInteraction,
  type Interaction,
  type InteractionItem,
  type InteractionIssueCode,
  type InteractionType,
} from '@slideforge/shared';
import { useStrings } from '../i18n/context.js';
import { parseInteractionRows } from '../interactions/bulk.js';
import type { SlideForgeState } from '../state/useSlideForge.js';

const TYPES: readonly InteractionType[] = ['tabs', 'accordion', 'timeline'];

export function InteractionPanel({ state }: { readonly state: SlideForgeState }) {
  const t = useStrings();
  const base = useId();
  const { selectedSlideId } = state;
  const interaction = state.interactionFor(selectedSlideId);
  const [selectedItemId, setSelectedItemId] = useState<string | undefined>();
  const [bulk, setBulk] = useState('');
  const [markerNotice, setMarkerNotice] = useState('');
  const persist = async (value: Interaction | undefined) => {
    const result = await state.setInteraction(selectedSlideId ?? '', value);
    setMarkerNotice(result.ok ? '' : result.message);
  };

  if (!selectedSlideId) {
    return (
      <section className="sf-panel" aria-labelledby={`${base}-heading`}>
        <h2 id={`${base}-heading`}>{t.interactionHeading}</h2>
        <p>{t.noSlideSelected}</p>
      </section>
    );
  }

  if (!interaction) {
    return (
      <section className="sf-panel" aria-labelledby={`${base}-heading`}>
        <h2 id={`${base}-heading`}>{t.interactionHeading}</h2>
        <p>{t.interactionChooseType}</p>
        <div className="sf-interaction-gallery">
          {TYPES.map((type) => (
            <button
              type="button"
              key={type}
              onClick={() => void persist(starterInteraction(type, selectedSlideId))}
            >
              <InteractionThumbnail type={type} />
              <strong>{t.interactionTypeNames[type]}</strong>
              <span>{t.interactionTypeDescriptions[type]}</span>
            </button>
          ))}
        </div>
      </section>
    );
  }

  const effectiveItemId = interaction.items.some((item) => item.id === selectedItemId)
    ? selectedItemId
    : interaction.items[0]?.id;
  const selectedItem = interaction.items.find((item) => item.id === effectiveItemId);
  const deckMedia = state.deck.parsed?.deck.media ?? [];
  const update = (next: Interaction) => void persist(interactionSchema.parse(next));
  const updateItem = (next: InteractionItem) =>
    update({ ...interaction, items: interaction.items.map((item) => (item.id === next.id ? next : item)) });
  const issues = validateInteraction(interaction);

  const importRows = (source: string) => {
    const rows = parseInteractionRows(source);
    if (rows.length === 0) return;
    const items = rows.map((row) => ({
      ...createInteractionItem(uniqueId('item')),
      label: row.label,
      shortLabel: row.label,
      body: row.body,
      ...(interaction.type === 'timeline' ? { date: { display: row.date, sortKey: row.date } } : {}),
    }));
    update({ ...interaction, items: [...interaction.items, ...items] });
    setSelectedItemId(items[0]?.id);
    setBulk('');
  };

  return (
    <section className="sf-panel" aria-labelledby={`${base}-heading`}>
      <div className="sf-panel-heading-row">
        <h2 id={`${base}-heading`}>{t.interactionHeading}</h2>
        <button type="button" className="sf-danger" onClick={() => void persist(undefined)}>
          {t.removeInteraction}
        </button>
      </div>
      {markerNotice ? <p role="status" className="sf-warning">{markerNotice}</p> : null}

      <div className="sf-field">
        <label htmlFor={`${base}-type`}>{t.interactionType}</label>
        <select
          id={`${base}-type`}
          value={interaction.type}
          onChange={(event) => update(changeType(interaction, event.target.value as InteractionType))}
        >
          {TYPES.map((type) => <option key={type} value={type}>{t.interactionTypeNames[type]}</option>)}
        </select>
      </div>
      <div className="sf-field">
        <label htmlFor={`${base}-title`}>{t.interactionTitle}</label>
        <input id={`${base}-title`} value={interaction.title} onChange={(event) => update({ ...interaction, title: event.target.value })} />
      </div>
      <div className="sf-field">
        <label htmlFor={`${base}-description`}>{t.interactionDescription}</label>
        <textarea id={`${base}-description`} value={interaction.description} onChange={(event) => update({ ...interaction, description: event.target.value })} />
      </div>

      <fieldset className="sf-interaction-settings">
        <legend>{t.interactionNavigation}</legend>
        <label className="sf-check"><input type="checkbox" checked={interaction.navigation.showPrevNext} onChange={(event) => update({ ...interaction, navigation: { ...interaction.navigation, showPrevNext: event.target.checked } })} />{t.interactionShowPrevNext}</label>
        <label className="sf-check"><input type="checkbox" checked={interaction.navigation.requireSequential} onChange={(event) => update({ ...interaction, navigation: { ...interaction.navigation, requireSequential: event.target.checked } })} />{t.interactionRequireSequential}</label>
        <label className="sf-check"><input type="checkbox" checked={interaction.navigation.trackViewedItems} onChange={(event) => update({ ...interaction, navigation: { ...interaction.navigation, trackViewedItems: event.target.checked } })} />{t.interactionTrackViewed}</label>
      </fieldset>

      <fieldset className="sf-interaction-settings">
        <legend>{t.interactionCompletion}</legend>
        <select aria-label={t.interactionCompletion} value={interaction.completion.rule} onChange={(event) => update({ ...interaction, completion: { ...interaction.completion, rule: event.target.value as Interaction['completion']['rule'] } })}>
          <option value="AllItemsViewed">{t.interactionAllViewed}</option>
          <option value="NItemsViewed">{t.interactionNViewed}</option>
          <option value="None">{t.interactionNoCompletion}</option>
        </select>
        {interaction.completion.rule === 'NItemsViewed' ? (
          <label>{t.interactionThreshold}<input type="number" min={1} max={Math.max(1, interaction.items.length)} value={interaction.completion.threshold} onChange={(event) => update({ ...interaction, completion: { ...interaction.completion, threshold: Number(event.target.value) } })} /></label>
        ) : null}
      </fieldset>

      <fieldset className="sf-interaction-settings">
        <legend>{t.interactionTheme}</legend>
        <label>
          <input type="radio" name={`${base}-theme`} checked={interaction.theme.colorSource === 'inherit-from-deck'} onChange={() => update({ ...interaction, theme: { ...interaction.theme, colorSource: 'inherit-from-deck' } })} />
          {t.interactionInheritTheme}
        </label>
        <label>
          <input type="radio" name={`${base}-theme`} checked={interaction.theme.colorSource === 'custom'} onChange={() => update({ ...interaction, theme: { ...interaction.theme, colorSource: 'custom' } })} />
          {t.interactionCustomTheme}
        </label>
        {interaction.theme.colorSource === 'custom' ? (
          <div className="sf-color-row">
            <label>{t.interactionAccent}<input type="color" value={interaction.theme.palette.accent} onChange={(event) => updatePalette(interaction, update, 'accent', event.target.value)} /></label>
            <label>{t.interactionSurface}<input type="color" value={interaction.theme.palette.surface} onChange={(event) => updatePalette(interaction, update, 'surface', event.target.value)} /></label>
            <label>{t.interactionText}<input type="color" value={interaction.theme.palette.text} onChange={(event) => updatePalette(interaction, update, 'text', event.target.value)} /></label>
          </div>
        ) : null}
      </fieldset>

      <h3>{t.interactionItems}</h3>
      <div className="sf-interaction-editor">
        <div>
          <ol className="sf-interaction-item-list">
            {interaction.items.map((item, index) => (
              <li key={item.id}>
                <button type="button" className={item.id === effectiveItemId ? 'sf-current' : ''} onClick={() => setSelectedItemId(item.id)}>{item.label || t.interactionUntitledItem}</button>
                <button type="button" aria-label={`${t.interactionMoveUp}: ${item.label}`} disabled={index === 0} onClick={() => update({ ...interaction, items: move(interaction.items, index, index - 1) })}>↑</button>
                <button type="button" aria-label={`${t.interactionMoveDown}: ${item.label}`} disabled={index === interaction.items.length - 1} onClick={() => update({ ...interaction, items: move(interaction.items, index, index + 1) })}>↓</button>
              </li>
            ))}
          </ol>
          <button type="button" onClick={() => {
            const item = createInteractionItem(uniqueId('item'));
            const next = interaction.type === 'timeline' ? { ...item, date: { display: '', sortKey: '' } } : item;
            update({ ...interaction, items: [...interaction.items, next] });
            setSelectedItemId(next.id);
          }}>{t.interactionAddItem}</button>
        </div>

        {selectedItem ? (
          <div className="sf-interaction-item-editor">
            <div className="sf-field"><label htmlFor={`${base}-item-label`}>{t.interactionItemLabel}</label><input id={`${base}-item-label`} value={selectedItem.label} onChange={(event) => updateItem({ ...selectedItem, label: event.target.value })} /></div>
            <div className="sf-field"><label htmlFor={`${base}-item-short`}>{t.interactionItemShortLabel}</label><input id={`${base}-item-short`} value={selectedItem.shortLabel} onChange={(event) => updateItem({ ...selectedItem, shortLabel: event.target.value })} /></div>
            <div className="sf-field"><label htmlFor={`${base}-item-body`}>{t.interactionItemBody}</label><textarea id={`${base}-item-body`} value={selectedItem.body} onChange={(event) => updateItem({ ...selectedItem, body: event.target.value })} /></div>
            <div className="sf-field"><label htmlFor={`${base}-item-latex`}>{t.interactionItemLatex}</label><input id={`${base}-item-latex`} value={selectedItem.latex} onChange={(event) => updateItem({ ...selectedItem, latex: event.target.value })} /></div>
            {interaction.type === 'timeline' ? <div className="sf-field"><label htmlFor={`${base}-item-date`}>{t.interactionItemDate}</label><input id={`${base}-item-date`} value={selectedItem.date?.display ?? ''} onChange={(event) => updateItem({ ...selectedItem, date: { display: event.target.value, sortKey: event.target.value } })} /></div> : null}
            <div className="sf-field">
              <label htmlFor={`${base}-item-media-kind`}>{t.interactionItemMediaKind}</label>
              <select
                id={`${base}-item-media-kind`}
                value={selectedItem.media.kind}
                onChange={(event) => updateItem({
                  ...selectedItem,
                  media: {
                    ...selectedItem.media,
                    kind: event.target.value as InteractionItem['media']['kind'],
                    ...(event.target.value === 'none' ? { assetId: undefined } : {}),
                  },
                })}
              >
                <option value="none">{t.interactionMediaTypeNames['none']}</option>
                <option value="image">{t.interactionMediaTypeNames['image']}</option>
                <option value="video">{t.interactionMediaTypeNames['video']}</option>
                <option value="audio">{t.interactionMediaTypeNames['audio']}</option>
              </select>
            </div>
            {selectedItem.media.kind !== 'none' ? (
              <>
                <div className="sf-field">
                  <label htmlFor={`${base}-item-media-asset`}>{t.interactionItemMediaAsset}</label>
                  <select
                    id={`${base}-item-media-asset`}
                    value={selectedItem.media.assetId ?? ''}
                    onChange={(event) => updateItem({ ...selectedItem, media: { ...selectedItem.media, assetId: event.target.value || undefined } })}
                  >
                    <option value="">{t.interactionItemMediaAsset}</option>
                    {deckMedia.map((asset) => <option key={asset.id} value={asset.id}>{asset.partName}</option>)}
                  </select>
                  {deckMedia.length === 0 ? <small>{t.interactionReadDeckForMedia}</small> : null}
                </div>
                <div className="sf-field"><label htmlFor={`${base}-item-media-alt`}>{t.interactionItemAltText}</label><input id={`${base}-item-media-alt`} value={selectedItem.media.altText} onChange={(event) => updateItem({ ...selectedItem, media: { ...selectedItem.media, altText: event.target.value } })} /></div>
                <div className="sf-field"><label htmlFor={`${base}-item-media-caption`}>{t.interactionItemCaption}</label><input id={`${base}-item-media-caption`} value={selectedItem.media.caption} onChange={(event) => updateItem({ ...selectedItem, media: { ...selectedItem.media, caption: event.target.value } })} /></div>
              </>
            ) : null}
            <button type="button" className="sf-danger" onClick={() => update({ ...interaction, items: interaction.items.filter((item) => item.id !== selectedItem.id) })}>{t.interactionRemoveItem}</button>
          </div>
        ) : null}
      </div>

      <details>
        <summary>{t.interactionBulkEntry}</summary>
        <p>{t.interactionBulkHelp}</p>
        <textarea aria-label={t.interactionBulkEntry} value={bulk} onChange={(event) => setBulk(event.target.value)} />
        <div className="sf-actions">
          <button type="button" disabled={!bulk.trim()} onClick={() => importRows(bulk)}>{t.interactionApplyBulk}</button>
          <label className="sf-file-button">{t.interactionImportCsv}<input type="file" accept=".csv,text/csv,text/tab-separated-values" onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) void file.text().then(importRows);
            event.target.value = '';
          }} /></label>
        </div>
      </details>

      <section aria-labelledby={`${base}-preview-heading`}>
        <h3 id={`${base}-preview-heading`}>{t.interactionPreview}</h3>
        <InteractionPreview interaction={interaction} />
      </section>

      <section aria-labelledby={`${base}-validation-heading`}>
        <h3 id={`${base}-validation-heading`}>{t.validationHeading}</h3>
        {issues.length === 0 ? <p>{t.validationClean}</p> : <ul>{issues.map((issue, index) => <li key={`${issue.code}-${issue.itemId ?? index}`}>{t.interactionIssueNames[issue.code]}</li>)}</ul>}
      </section>
    </section>
  );
}

function starterInteraction(type: InteractionType, slideId: string): Interaction {
  const interaction = createInteraction(type, `interaction-${slideId}`);
  const first = createInteractionItem(uniqueId('item'));
  return interactionSchema.parse({
    ...interaction,
    title: type === 'accordion' ? 'FAQ' : '',
    items: [{ ...first, ...(type === 'timeline' ? { date: { display: '', sortKey: '' } } : {}) }],
  });
}

function changeType(interaction: Interaction, type: InteractionType): Interaction {
  return interactionSchema.parse({
    ...interaction,
    type,
    layout: { ...interaction.layout, orientation: type === 'timeline' ? 'vertical' : 'horizontal' },
    items: interaction.items.map((item) => ({
      ...item,
      ...(type === 'timeline' && !item.date ? { date: { display: '', sortKey: '' } } : {}),
    })),
  });
}

function updatePalette(interaction: Interaction, update: (next: Interaction) => void, key: 'accent' | 'surface' | 'text', value: string): void {
  update({ ...interaction, theme: { ...interaction.theme, palette: { ...interaction.theme.palette, [key]: value } } });
}

function move<T>(items: readonly T[], from: number, to: number): T[] {
  const next = [...items];
  const [item] = next.splice(from, 1);
  if (item !== undefined) next.splice(to, 0, item);
  return next;
}

function uniqueId(prefix: string): string {
  return `${prefix}-${globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2, 10)}`;
}

function InteractionThumbnail({ type }: { readonly type: InteractionType }) {
  return <span className={`sf-interaction-thumbnail sf-interaction-thumbnail-${type}`} aria-hidden="true"><i /><i /><i /></span>;
}

function InteractionPreview({ interaction }: { readonly interaction: Interaction }) {
  const [active, setActive] = useState(0);
  const item = interaction.items[active] ?? interaction.items[0];
  const style = useMemo(
    () => interaction.theme.colorSource === 'custom' ? {
      '--sf-preview-accent': interaction.theme.palette.accent,
      '--sf-preview-surface': interaction.theme.palette.surface,
      '--sf-preview-text': interaction.theme.palette.text,
    } as React.CSSProperties : undefined,
    [interaction.theme],
  );
  return (
    <div className={`sf-interaction-preview sf-interaction-preview-${interaction.type}`} style={style}>
      <strong>{interaction.title}</strong>
      <div role="group" aria-label={interaction.title}>
        {interaction.items.map((candidate, index) => <button type="button" key={candidate.id} aria-pressed={index === active} onClick={() => setActive(index)}>{interaction.type === 'timeline' ? candidate.date?.display : candidate.shortLabel || candidate.label}</button>)}
      </div>
      {item ? <section><h4>{item.label}</h4><p>{item.body}</p></section> : null}
    </div>
  );
}

export type { InteractionIssueCode };
